import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileUp, RefreshCw } from "lucide-react";
import { api, apiError } from "../../lib/api";

export const BookFileTab = () => {
  const [manifest, setManifest] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    api.get("/pages/manifest").then((r) => setManifest(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (manifest?.status === "converting") {
      const t = setInterval(load, 4000);
      return () => clearInterval(t);
    }
  }, [manifest?.status, load]);

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".odt")) {
      toast.error("Please choose the .odt file of your book.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/book-file", fd);
      toast.success("Book received — rendering its pages now");
      load();
    } catch (e) {
      toast.error(apiError(e, "Upload failed."));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const converting = manifest?.status === "converting";

  return (
    <div className="max-w-2xl">
      <p className="text-sm leading-relaxed text-[#A39E93]">
        Your book lives here as its original .odt file. Every page is rendered into a fixed image — so the exact font, spacing and layout
        you wrote can never shift, on the website or in the app. When you revise the book, upload the new .odt here.
      </p>

      <div className="mt-8 rounded-sm border border-[#262930] bg-[#121418] p-6">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#6E6B63]">Current book</p>
        <p data-testid="book-file-status" className="mt-2 font-display text-xl text-[#F4F1EA]">
          {manifest === null && "Checking…"}
          {manifest?.status === "none" && "No book file yet"}
          {manifest?.status === "converting" && "Rendering pages…"}
          {manifest?.status === "ready" && `${manifest.source_filename || "book.odt"} · ${manifest.count} pages`}
          {manifest?.status === "failed" && "Last conversion failed"}
        </p>
        {manifest?.status === "ready" && manifest.converted_at && (
          <p className="mt-1 font-mono text-xs text-[#6E6B63]">rendered {new Date(manifest.converted_at).toLocaleString()}</p>
        )}
        {manifest?.status === "failed" && manifest.error && (
          <p className="mt-2 break-words font-mono text-xs text-[#C85A32]">{manifest.error}</p>
        )}

        <input ref={inputRef} data-testid="admin-book-file-upload" type="file" accept=".odt" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        <button
          data-testid="admin-book-file-upload-btn"
          onClick={() => inputRef.current?.click()}
          disabled={busy || converting}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#D4A373] px-6 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#0B0C0E] transition-colors hover:bg-[#E0A96D] disabled:opacity-50"
        >
          {busy || converting ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <FileUp className="h-3.5 w-3.5" />}
          {converting ? "Rendering…" : manifest?.status === "ready" ? "Upload new .odt" : "Upload .odt"}
        </button>
        <p className="mt-4 text-xs leading-relaxed text-[#6E6B63]">
          After uploading a revised book, open “The app” tab and press Rebuild so the Windows app includes the newest pages.
        </p>
      </div>
    </div>
  );
};
